import VerticallySplitLayout from "@components/verticallySplitLayout";
import { IonItem, IonLabel, IonList, IonListHeader, IonNote } from "@ionic/react";
import { calculateDetailedDueCounts, getFSRSCardsFromKnols } from "@lib/fsrs/dueCounts";
import MapView from "@lib/mapView";
import { IDeck } from "@models/deck";
import { IKnol } from "@models/knol";
import { ICardResponse } from "@screens/review/types";
import Num from "components/number";
import ResponseHistogram from "components/responseHistogram";
import { TResponseName } from "globals";
import { millis2TimeString } from "lib";
import L10n from "localization";
import { Fragment, useMemo } from "react";
import Style from "style";
import DeckStats from "./deckStats";

export type ResponseCounts = {
  [name in TResponseName]?: number;
};

export function responseStats(responses: ICardResponse[]) {
  const responseCounts: ResponseCounts = {};
  for (const { response } of responses) {
    responseCounts[response] = (responseCounts[response] ?? 0) + 1;
  }

  const totalReviews = responses.length;

  let totalMillis = 0;
  for (const { durationMs } of responses) {
    totalMillis += durationMs;
  }

  return {
    totalReviews,
    totalMillis,
    responseCounts,
  };
}

interface SessionStatsProps {
  totalMillis: number;
  responseCounts: ResponseCounts;
  totalReviews: number;
  numReviewsToday?: number;
  studyGoal?: number;
  gradeChangeToday: number | null;
  numAverageReviewsPerDay: number | undefined;
  splitView: boolean;
  isOmniReview: boolean;
  deck?: IDeck;
  knols?: MapView<string, IKnol>;
  responses?: ICardResponse[];
  isFSRS: boolean;
  isFolderReview?: boolean;
}

export default function SessionStats({
  totalMillis,
  responseCounts,
  totalReviews,
  numReviewsToday,
  studyGoal,
  gradeChangeToday,
  numAverageReviewsPerDay,
  splitView,
  isOmniReview,
  deck,
  knols,
  responses,
  isFSRS,
}: SessionStatsProps) {
  const totalTimeStr = millis2TimeString(totalMillis);

  // Calculate FSRS due counts using centralized utility
  const fsrsDueCounts = useMemo(() => {
    if (!isFSRS || !knols) {
      return null;
    }

    const cards = getFSRSCardsFromKnols(knols.values());
    const counts = calculateDetailedDueCounts(cards);

    if (!counts) {
      return null;
    }

    return {
      dueNow: counts.now,
      dueWithin24hr: counts.within24hr,
      dueTomorrow: counts.tomorrow,
      dueWithin1wk: counts.within1wk,
      newCards: counts.new,
    };
  }, [isFSRS, knols]);

  const getContentLayout = () => {
    if (splitView) {
      return (
        <VerticallySplitLayout
          left={
            <Fragment key="leftComponents">
              {isFSRS && !isOmniReview && dueCardsSection()}
              {isOmniReview ? sessionStats() : deckStatsSection()}
              {(isOmniReview || splitView) && dailyProgressSection()}
            </Fragment>
          }
          right={
            <>
              {!isOmniReview && sessionStats()}
              {!isFSRS && (
                <ResponseHistogram
                  height={isOmniReview ? "8rem" : undefined}
                  counts={responseCounts}
                />
              )}
            </>
          }
        />
      );
    }
    return (
      <Fragment key="singleColumnLayout">
        {...(isOmniReview
          ? [sessionStats(), ...(isFSRS ? [] : [studyGoalSection()])]
          : [
              ...(isFSRS ? [dueCardsSection()] : []),
              deckStatsSection(),
              ...(isFSRS ? [dailyProgressSection()] : [studyGoalSection()]),
              sessionStats(),
            ])}
        {!isFSRS && <ResponseHistogram key="resp-historgram" counts={responseCounts} />}
      </Fragment>
    );
  };

  const sessionStats = () => (
    <IonList key="sessionStatsList">
      <IonListHeader>{L10n.localize((s) => s.review.session)}</IonListHeader>
      {commonSessionItems()}
    </IonList>
  );

  const dueCardsSection = () => {
    const hasDueCards =
      fsrsDueCounts &&
      (fsrsDueCounts.dueNow > 0 || fsrsDueCounts.dueWithin24hr > 0 || fsrsDueCounts.newCards > 0);

    if (!hasDueCards) return null;

    return (
      <IonList key="dueCardsSection" style={{ marginBottom: "1rem" }}>
        <IonListHeader
          style={{
            color: Style.colors.fail,
          }}
        >
          {L10n.localize((s) => s.review.cardsDue)}
        </IonListHeader>
        {fsrsDueCounts.dueNow > 0 && (
          <IonItem
            style={{
              backgroundColor: "rgba(255, 0, 0, 0.05)",
              borderLeft: `4px solid ${Style.colors.fail}`,
            }}
          >
            <IonLabel style={{ fontWeight: "bold" }}>
              {L10n.localize((s) => s.review.stillDueNow)}
            </IonLabel>
            <IonNote slot="end">
              <span
                style={{
                  color: Style.colors.fail,
                  fontWeight: "bold",
                  fontSize: "1.1em",
                }}
              >
                <Num num={fsrsDueCounts.dueNow} />
              </span>
            </IonNote>
          </IonItem>
        )}
        {fsrsDueCounts.dueWithin24hr > 0 && (
          <IonItem
            style={{
              backgroundColor: "rgba(255, 165, 0, 0.05)",
              borderLeft: `4px solid ${Style.colors.hard}`,
            }}
          >
            <IonLabel style={{ fontWeight: "bold" }}>
              {L10n.localize((s) => s.review.stillDueToday)}
            </IonLabel>
            <IonNote slot="end">
              <span
                style={{
                  color: Style.colors.hard,
                  fontWeight: "bold",
                  fontSize: "1.1em",
                }}
              >
                <Num num={fsrsDueCounts.dueWithin24hr} />
              </span>
            </IonNote>
          </IonItem>
        )}
        {fsrsDueCounts.newCards > 0 && (
          <IonItem
            style={{
              backgroundColor: "rgba(88, 83, 148, 0.05)",
              borderLeft: `4px solid ${Style.colors.grades.NEW}`,
            }}
          >
            <IonLabel style={{ fontWeight: "bold" }}>
              {L10n.localize((s) => s.review.newCardsToReview)}
            </IonLabel>
            <IonNote slot="end">
              <span
                style={{
                  color: Style.colors.grades.NEW,
                  fontWeight: "bold",
                  fontSize: "1.1em",
                }}
              >
                <Num num={fsrsDueCounts.newCards} />
              </span>
            </IonNote>
          </IonItem>
        )}
      </IonList>
    );
  };

  const dailyProgressSection = () => (
    <IonList key="dailyProgressSection">
      <IonListHeader>{L10n.localize((s) => s.review.dailyProgress)}</IonListHeader>
      <IonItem lines={splitView ? "none" : undefined}>
        <IonLabel>{L10n.localize((s) => s.review.cardsStudiedToday)}</IonLabel>
        <IonNote slot="end">
          <Num num={numReviewsToday} />
          {studyGoal && `/${studyGoal}`}
        </IonNote>
      </IonItem>
    </IonList>
  );

  const studyGoalSection = () => (
    <IonList key="studyGoalSection">
      <IonListHeader>{L10n.localize((s) => s.general.studyGoal)}</IonListHeader>
      <IonItem lines={splitView ? "none" : undefined}>
        <IonLabel>{L10n.localize((s) => s.review.numToday)}</IonLabel>
        <IonNote slot="end">
          <Num num={numReviewsToday} />
          {studyGoal && `/${studyGoal}`}
        </IonNote>
      </IonItem>
    </IonList>
  );

  const deckStatsSection = () =>
    !isOmniReview ? (
      <Fragment key="deckStatsSection">
        {splitView && <IonListHeader>{L10n.localize((s) => s.deck.singular)}</IonListHeader>}
        <DeckStats
          gradeChangeToday={gradeChangeToday}
          numAverageReviewsPerDay={numAverageReviewsPerDay}
          deck={deck}
          knols={knols}
          responses={responses}
        />
      </Fragment>
    ) : null;

  const commonSessionItems = () => {
    const avgTimePerCard = totalReviews > 0 ? Math.round(totalMillis / totalReviews / 1000) : 0;

    return (
      <Fragment key="sessionItems">
        <IonItem>
          <IonLabel>{L10n.localize((s) => s.review.total)}</IonLabel>
          <IonNote slot="end">
            <Num num={totalReviews} />
          </IonNote>
        </IonItem>
        <IonItem>
          <IonLabel>{L10n.localize((s) => s.review.totalTime)}</IonLabel>
          <IonNote slot="end">{totalTimeStr}</IonNote>
        </IonItem>
        {totalReviews > 0 && (
          <IonItem>
            <IonLabel>{L10n.localize((s) => s.review.averageTimePerCard)}</IonLabel>
            <IonNote slot="end">{avgTimePerCard}s</IonNote>
          </IonItem>
        )}
      </Fragment>
    );
  };

  return <div>{getContentLayout()}</div>;
}
